/**
 * Registers a new block provided a unique name and an object defining its behavior.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-registration/
 */
import { registerBlockType, createBlock } from '@wordpress/blocks';

import { Icon } from "@wordpress/components";

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * All files containing `style` keyword are bundled together. The code used
 * gets applied both to the front of your site and to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './style.scss';

/**
 * Internal dependencies
 */
import Edit from './edit';
import save from './save';
import metadata from './block.json';

const imgwb_icon = (
<svg width="20" height="20" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
<title>Image+</title>
	<svg style={{
		opacity: 0
	}}>
		<rect x="20"
			y="70"
			width="120"
			height="100"
			fill="white"
			stroke-width="10"
			stroke="black" />
		<path d="M 35,95 L 35,155 L 125, 155 Z" fill="black" stroke-width="0" />
		<animate
			attributeName="opacity" attributeType="css"
			keyTimes="0; 1"
			values="0; 1"
			begin="0s;10s"
			dur="0.2s"
			fill="freeze"
		/>
	</svg>
	<svg x="20" y="-20" style={{
		opacity: 0
	}}>
		<rect x="20"
			y="70"
			width="120"
			height="100"
			fill="white"
			stroke-width="10"
			stroke="black" />
		<path d="M 35,95 L 35,155 L 125, 155 Z" fill="black" stroke-width="0" />
		<animate
			attributeName="opacity" attributeType="css"
			keyTimes="0; 1"
			values="0; 1"
			begin="0.2s;10.2s"
			dur="0.2s"
			fill="freeze"
		/>
	</svg>
	<svg x="40" y="-40" style={{
		opacity: 0
	}}>
		<rect x="20"
			y="70"
			width="120"
			height="100"
			fill="white"
			stroke-width="10"
			stroke="black" />
		<path d="M 35,95 L 35,155 L 125, 155 Z" fill="black" stroke-width="0" />
		<animate
			attributeName="opacity" attributeType="css"
			keyTimes="0; 1"
			values="0; 1"
			begin="0.4s;10.4s"
			dur="0.2s"
			fill="freeze"
		/>
	</svg>
	<circle class="imgwb-icon-circle" cx="145" cy="65" r="0" fill="black" stroke-width="0" />
</svg>
);

// rotate
function get_example_src() {
	return metadata.example_src + '&ts=' + Date.now();
}

/**
 * Every block starts by registering a new block type definition.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-registration/
 */
registerBlockType( metadata.name, {

	// animated
	icon: imgwb_icon,

	/**
	 * @see ./edit.js
	 */
	edit: Edit,

	/**
	 * @see ./save.js
	 */
	save,

	/**
	 *	core/image
	 * 	{
			"alt": "Blue Pill",
			"id": 28,
			"link": "http://imgwb.local/sample-page/blue-pill/",
			"caption": "Here's the caption",
			"url": "http://imgwb.local/wp-content/uploads/2022/08/blue-pill-854x1024.jpg",
			"sizeSlug": "large",
			"linkDestination": "custom",
			"href": "https://example.com"
		}

		TODO: transform from cover (with caption), gallery (multiple variants)
	 */
	transforms: {
		from: [
			{
				type: 'block',
				blocks: [ 'core/image' ],
				transform: function(attributes) {
					//console.log('transform from', attributes);

					const from_img = document.querySelector('.is-selected img');
					const text = attributes.caption || attributes.alt || 'Call To Action';
					const font_size = parseInt(from_img.width / Math.max(8, text.length));

					const imgwb_attributes = {
						from_src: attributes.url,
						// new gets replaced when we get real image & variant data back from server
						image_id: 'new',
						link: attributes.href,
						variant_id: 'new',
						variants: {
							'new': {
								label: attributes.alt || 'New Image',
								width: from_img.width,
								height: from_img.height,
								variant_type: 'primary',
								variant_template: 'focus',
								params: {
									focus: {
										box: '400400200200:Center',
										zoom: 1,
										duration: 2
									},
									captions: [
										{
											text: text,
											x: 0,
											y: from_img.height - font_size * 2,
											font_family: "Montserrat",
											font_size: font_size,
											font_size_auto: true,
											font_weight: 700,
											width: from_img.width,
											height: font_size * 2,
											bgcolor: "#7b171ade",
											color: "#ffffff",
											begin: 1,
											end: 8
										}
									]
								}
							}
						}
					}
					return createBlock(metadata.name, imgwb_attributes);
				}
			}
		],
		to: [
			{
				type: 'block',
				blocks: [ 'core/image' ],
				transform: function(attributes) {
					return createBlock('core/image', {
						url: attributes.to_src,
						caption: 'Image Without Benefits'
					});
				}
			}
		]
	},

	// does this inherit transformed attributes.url from core/image so we can preview using the imported image?
	example: {
		attributes: {
			from_src: get_example_src(),
			image_id: 'example',
			variant_id: 'example',
			variants: {
				'example': {
					width: 1300,
					height: 1558,
					variant_type: 'primary',
					variant_template: 'focus',
					params: {
						focus: {
							box: '400400200200:Center',
							zoom: -2.5,
							duration: 2
						},
						captions: [
							{
								text: "You Know It Makes Sense",
								x: 0,
								y: 1400,
								font_family: "Montserrat",
								font_size: 90,
								font_size_auto: true,
								font_weight: 700,
								width: 1300,
								height: 158,
								bgcolor: "#7b171ade",
								color: "#ffffff",
								begin: 1,
								end: 8
							}
						]
					}
				}
			}
		}
	}
} );
